import { z } from 'zod';

/**
 * Site Configuration Schema
 *
 * Defines the physical site location, facility type, and resilience parameters.
 * Facility type is used for smart load interpolation (8760 → 35040).
 */

/**
 * Facility types for load pattern interpolation
 * Each type has characteristic sub-hourly demand patterns
 */
export const FACILITY_TYPES = {
  commercial_office: {
    label: 'Commercial Office',
    description: 'Office buildings with HVAC cycling and morning/evening ramps',
    variance: 0.12,
  },
  industrial: {
    label: 'Industrial / Manufacturing',
    description: 'High variance with equipment startups and random spikes',
    variance: 0.25,
  },
  retail: {
    label: 'Retail / Store',
    description: 'Steady load with door/lighting cycles',
    variance: 0.15,
  },
  warehouse: {
    label: 'Warehouse / Distribution',
    description: 'Flat load with occasional forklift charging',
    variance: 0.08,
  },
  ev_charging: {
    label: 'EV Charging Station',
    description: 'High peaks with fast ramps from vehicle arrivals',
    variance: 0.40,
  },
  data_center: {
    label: 'Data Center',
    description: 'Ultra-flat load smoothed by UPS systems',
    variance: 0.03,
  },
  restaurant: {
    label: 'Restaurant / Food Service',
    description: 'Kitchen equipment cycles with meal rush peaks',
    variance: 0.20,
  },
  hospital: {
    label: 'Hospital / Healthcare',
    description: 'Critical load with equipment cycles, 24/7 operation',
    variance: 0.10,
  },
  school: {
    label: 'School / University',
    description: 'Seasonal patterns with class schedule cycles',
    variance: 0.18,
  },
  mixed_use: {
    label: 'Mixed Use / Other',
    description: 'Blended pattern for multi-tenant or unknown facilities',
    variance: 0.15,
  },
};

export const FacilityTypeEnum = z.enum([
  'commercial_office',
  'industrial',
  'retail',
  'warehouse',
  'ev_charging',
  'data_center',
  'restaurant',
  'hospital',
  'school',
  'mixed_use',
]);

export const SiteSchema = z.object({
  // Required location fields
  latitude: z.number()
    .min(-90, 'Latitude must be >= -90')
    .max(90, 'Latitude must be <= 90')
    .refine(
      (val) => val !== 0,
      { message: 'Latitude cannot be 0 (invalid location)' }
    ),

  longitude: z.number()
    .min(-180, 'Longitude must be >= -180')
    .max(180, 'Longitude must be <= 180')
    .refine(
      (val) => val !== 0,
      { message: 'Longitude cannot be 0 (invalid location)' }
    ),

  // Facility type for load interpolation patterns
  facility_type: FacilityTypeEnum.default('mixed_use'),

  // Off-grid mode flag
  off_grid_flag: z.boolean().default(false),

  // Resilience parameters (Vortx Flux specific)
  min_outage_survival_hours: z.number()
    .min(0, 'Outage survival hours cannot be negative')
    .optional(),

  critical_load_fraction: z.number()
    .min(0, 'Critical load fraction must be >= 0')
    .max(1, 'Critical load fraction must be <= 1')
    .optional(),

  // Address (optional, for display purposes)
  address: z.string().optional(),

  // Land constraints (optional)
  land_acres: z.number().positive().optional(),
  roof_squarefeet: z.number().positive().optional(),
});

/**
 * Validates that latitude and longitude are not both 0 (Gulf of Guinea)
 * This is a common default value that indicates missing location data
 */
export const validateSiteLocation = (site) => {
  if (site.latitude === 0 && site.longitude === 0) {
    return {
      valid: false,
      error: 'Location cannot be 0,0 - please provide a valid address or coordinates',
    };
  }
  return { valid: true };
};

export default SiteSchema;
